import { Fragment, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Info } from "lucide-react";
import { Table, TableBody, TableCell, TableFooter, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { SearchFilterBar } from "@/components/shared/SearchFilterBar";
import { SortableTableHead, getNextSort, type SortDirection } from "@/components/shared/SortableTableHead";
import { NestedDrawerContainer } from "@/components/shared/NestedDrawerTable";
import { TabHeader } from "@/components/shared/TabHeader";
import { cn } from "@/lib/utils";
import { BI_TOOLTIPS } from "./tooltips";
import { KeywordProductHistory } from "./KeywordProductHistory";
import { KeywordSovCards, type SovScope } from "./KeywordSovCards";
import {
  ALL_CATEGORIES,
  NICHE,
  applyRowFilters,
  deriveKeywordSovView,
  footerTotals,
  rowsForChip,
  rowsForNiche,
  shareOf,
  type PickedCategoryFilter,
} from "./keywordSovView";
import {
  useGetBrandIntelligenceKeywordSov,
  type BiKeywordSovCategory,
  type BiKeywordSovRow,
  type BiKeywordSovSummary,
} from "@/resources/brand-intelligence/brand-intelligence.api";

const fmtVol = (n: number) => n.toLocaleString();
const fmtRev = (n: number) => (n >= 1_000_000 ? `$${(n / 1_000_000).toFixed(1)}M` : n >= 1_000 ? `$${Math.round(n / 1_000)}K` : `$${Math.round(n)}`);
const fmtPct = (n: number | null | undefined) => (n == null || !Number.isFinite(n) ? null : `${n.toFixed(1)}%`);
// Inline heat shading (matches prototype): tracked brand tinted primary, competitors green.
function heatBg(v: number | null, tone: "brand" | "green" | "none"): React.CSSProperties {
  if (v == null || tone === "none") return {};
  const alpha = Math.min(0.55, (v / 60) * 0.55);
  const varName = tone === "brand" ? "--primary" : "--status-success-strong";
  return { backgroundColor: `hsl(var(${varName}) / ${alpha.toFixed(3)})` };
}

type SortField = "kw" | "vol" | "rev" | string; // brand columns are `brand:{name}`

type MatrixFilters = { brand: string[] };

const NO_BRANDS: string[] = [];
const NO_CATEGORIES: BiKeywordSovCategory[] = [];
const EMPTY_SUMMARY: BiKeywordSovSummary = { keyword_count: 0, ranked_count: 0, volume_weighted_sov: null };

const Placeholder = ({ tone, children }: { tone: "muted" | "error"; children: string }) => (
  <div className={cn("rounded-sm border border-dashed py-12 text-center text-sm", tone === "error" ? "text-status-error-strong" : "text-muted-foreground")}>{children}</div>
);

export function KeywordSovTab({ brandId, choiceSaveCount = 0 }: { brandId: number; choiceSaveCount?: number }) {
  const { data, isLoading, isError, isPlaceholderData } = useGetBrandIntelligenceKeywordSov(brandId);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState<MatrixFilters>({ brand: [] });
  const [pickedChip, setPickedChip] = useState<string | null>(null);
  const [pickedCategoryFilter, setPickedCategoryFilter] = useState<PickedCategoryFilter | null>(null);
  const [seen, setSeen] = useState({ brandId, choiceSaveCount });
  const [sortField, setSortField] = useState<SortField | null>("vol");
  const [sortDir, setSortDir] = useState<SortDirection>("desc");

  // A brand switch or the user's own Save re-applies the default: adjust state during render, above the
  // early returns, so no frame shows the old view. Search, brand filter, sort and open row are kept.
  if (seen.brandId !== brandId || seen.choiceSaveCount !== choiceSaveCount) {
    setSeen({ brandId, choiceSaveCount });
    setPickedChip(null);
    setPickedCategoryFilter(null);
  }

  const tracked = data?.brand ?? "";
  const brands = data?.brands ?? NO_BRANDS;
  const categories = data?.categories ?? NO_CATEGORIES;

  const choice = data?.choice ?? null;
  const { chip, categoryFilter } = deriveKeywordSovView(choice, categories, pickedChip, pickedCategoryFilter);
  const isNiche = chip === NICHE && choice !== null;

  const selectedCategory = categories.find((c) => c.leaf_category_id === chip);
  const scope: SovScope = isNiche
    ? {
        kind: "niche",
        names: choice.leaf_category_ids.flatMap((id) => {
          const name = categories.find((c) => c.leaf_category_id === id)?.name;
          return name === undefined ? [] : [name];
        }),
      }
    : chip === ALL_CATEGORIES
      ? { kind: "all" }
      : { kind: "category", name: selectedCategory?.name ?? "" };
  // Subject of the no-keyword-set sentence only; the card and its tooltips read `scope`.
  const emptySubject =
    scope.kind === "niche" ? `This niche's ${scope.names.length} categories have` : `${scope.kind === "all" ? "All categories" : scope.name} has`;
  const summary = (isNiche ? choice : chip === ALL_CATEGORIES ? data?.all : selectedCategory) ?? EMPTY_SUMMARY;

  const chipOptions = useMemo(
    () => [
      ...categories.map((c) => ({ value: c.leaf_category_id, label: c.name, meta: fmtRev(c.brand_revenue) })),
      { value: ALL_CATEGORIES, label: "All categories" },
    ],
    [categories],
  );

  const handleChipChange = (next: string) => {
    setPickedChip(next);
    setPickedCategoryFilter({ ids: [], byChip: true });
  };

  const handleSort = (field: SortField) => {
    const next = getNextSort(sortField, sortDir, field);
    setSortField(next.field);
    setSortDir(next.direction);
  };

  const filtered = useMemo(
    () =>
      applyRowFilters(isNiche ? rowsForNiche(data?.keywords ?? [], choice.leaf_category_ids) : rowsForChip(data?.keywords ?? [], chip), {
        search,
        competitors: filters.brand,
        categories: categoryFilter,
      }),
    [data, chip, isNiche, choice, search, filters, categoryFilter],
  );

  const rows = useMemo(() => {
    const d: BiKeywordSovRow[] = [...filtered];
    if (!sortField) return d;
    const dir = sortDir === "asc" ? 1 : -1;
    d.sort((a, b) => {
      if (sortField === "kw") return dir * a.keyword.localeCompare(b.keyword);
      if (sortField === "vol") return dir * (a.monthly_volume - b.monthly_volume);
      if (sortField === "rev") {
        // A missing revenue sorts below every number, in either direction.
        if (a.kw_revenue === null || b.kw_revenue === null) return a.kw_revenue === b.kw_revenue ? 0 : a.kw_revenue === null ? 1 : -1;
        return dir * (a.kw_revenue - b.kw_revenue);
      }
      if (sortField.startsWith("brand:")) {
        const bn = sortField.slice(6);
        return dir * ((shareOf(a, bn) ?? -1) - (shareOf(b, bn) ?? -1));
      }
      return 0;
    });
    return d;
  }, [filtered, sortField, sortDir]);

  const footer = useMemo(() => footerTotals(rows, brands), [rows, brands]);

  if (isLoading || isPlaceholderData) return <Placeholder tone="muted">Loading keyword share of voice…</Placeholder>;
  if (isError || !data) return <Placeholder tone="error">Could not load Keyword SOV.</Placeholder>;
  if (!data.built) return <Placeholder tone="muted">Keyword share of voice hasn't been built yet. Use Refresh to build it.</Placeholder>;

  const noKeywordSet = summary.keyword_count === 0;

  return (
    <div className="space-y-6">
      <TabHeader
        title="Keyword SOV"
        description="Share of top organic positions versus competitors. Choose a category below, and then click any keyword to see which products drive that share."
        showDescription
        titleAdornment={
          <TooltipProvider delayDuration={150}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button type="button" aria-label="How SOV is calculated" className="text-muted-foreground hover:text-foreground">
                  <Info className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm text-left">
                <div className="space-y-2">
                  <p>
                    <span className="font-semibold text-foreground">How it's calculated:</span> every ASIN's organic rank gets a position weight of 1/rank (rank 1 = 1.0, rank 5 = 0.2, capped at rank 100). A brand's SOV = sum of its ASINs' weights ÷ total weight of all ranking ASINs.
                  </p>
                  <p>
                    <span className="font-semibold text-foreground">Keyword set:</span> each category reads its own keyword set; All categories reads every keyword the brand competes on, deduplicated across categories. Refreshed monthly.
                  </p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        }
      />

      <SearchFilterBar
        search={search}
        onSearchChange={setSearch}
        placeholder="Search keywords…"
        filterColumns={[
          {
            id: "brand",
            label: "Competitors",
            options: brands.filter((b) => b !== tracked && b !== "Other").map((b) => ({ value: b, label: b })),
          },
          {
            id: "category",
            label: "Category/ Niche",
            options: categories.map((c) => ({ value: c.leaf_category_id, label: c.name })),
            optionColumns: 2,
          },
        ]}
        activeFilters={{ ...filters, category: categoryFilter }}
        onFiltersChange={(f) => {
          setFilters({ brand: f.brand ?? [] });
          const next = f.category ?? [];
          if (next.length !== categoryFilter.length || next.some((id, i) => id !== categoryFilter[i])) {
            setPickedCategoryFilter({ ids: next, byChip: false });
          }
        }}
        quickFilters={chipOptions}
        quickFilterValue={chip}
        quickFilterValues={isNiche ? choice.leaf_category_ids : undefined}
        onQuickFilterChange={handleChipChange}
        quickFilterLabel="Categories"
        quickFilterMaxVisible={4}
      />

      <KeywordSovCards scope={scope} brand={tracked} summary={summary} />

      {noKeywordSet ? (
        <p className="rounded-sm border border-dashed py-12 text-center text-sm text-muted-foreground">
          {emptySubject} no keyword set: no keyword met the set's thresholds.
        </p>
      ) : (
        <Table stickyFooter className="table-fixed" style={{ width: 240 + 100 + 110 + brands.length * 120 }}>
          <colgroup>
            <col style={{ width: 240 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 110 }} />
            {brands.map((b) => <col key={b} style={{ width: 120 }} />)}
          </colgroup>
          <TableHeader>
            <TableRow>
              <SortableTableHead field="kw" activeField={sortField} direction={sortDir} onSort={handleSort} sticky className="px-4 py-3">Keyword</SortableTableHead>
              <SortableTableHead field="vol" activeField={sortField} direction={sortDir} onSort={handleSort} className="px-4 py-3 text-right">Mo. Vol.</SortableTableHead>
              <SortableTableHead field="rev" activeField={sortField} direction={sortDir} onSort={handleSort} tooltip={BI_TOOLTIPS.kwRevenue} className="px-4 py-3 text-right whitespace-nowrap">KW Revenue</SortableTableHead>
              {brands.map((b) => (
                <SortableTableHead key={b} field={`brand:${b}`} activeField={sortField} direction={sortDir} onSort={handleSort} className={cn("px-4 py-3 text-right whitespace-nowrap", b === "Other" && "text-muted-foreground")}>
                  {b}
                </SortableTableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => {
              const isOpen = expanded === r.keyword;
              const ChevronIcon = isOpen ? ChevronDown : ChevronRight;
              return (
                <Fragment key={r.keyword}>
                  <TableRow className="cursor-pointer" onClick={() => setExpanded(isOpen ? null : r.keyword)}>
                    <TableCell sticky className="px-4 py-3 font-medium text-foreground">
                      <span className="inline-flex items-center gap-1.5">
                        <ChevronIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                        {r.keyword}
                      </span>
                    </TableCell>
                    <TableCell className="px-4 py-3 text-right tabular-nums text-foreground">{fmtVol(r.monthly_volume)}</TableCell>
                    <TableCell className="px-4 py-3 text-right tabular-nums text-foreground">
                      {r.kw_revenue === null ? <span className="text-muted-foreground">—</span> : fmtRev(r.kw_revenue)}
                    </TableCell>
                    {brands.map((b) => {
                      const v = shareOf(r, b);
                      const tone = b === tracked ? "brand" : b === "Other" ? "none" : "green";
                      return (
                        <TableCell key={b} className="px-4 py-3 text-right tabular-nums text-foreground" style={heatBg(v, tone)}>
                          {fmtPct(v) ?? <span className="text-muted-foreground">—</span>}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {isOpen && (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={3 + brands.length} className="p-0 before:hidden">
                        <NestedDrawerContainer>
                          <KeywordProductHistory brandId={brandId} keyword={r.keyword} />
                        </NestedDrawerContainer>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
          <TableFooter>
            <TableRow className="hover:bg-transparent">
              <TableCell sticky className="px-4 py-3 font-semibold text-foreground bg-card">{footer.count} {footer.count === 1 ? "keyword" : "keywords"}</TableCell>
              <TableCell className="px-4 py-3 text-right tabular-nums font-semibold text-foreground">{fmtVol(footer.monthlyVolume)}</TableCell>
              <TableCell className="px-4 py-3 text-right tabular-nums font-semibold text-foreground">
                {footer.kwRevenue === null ? <span className="text-muted-foreground">—</span> : fmtRev(footer.kwRevenue)}
              </TableCell>
              {brands.map((b) => (
                <TableCell key={b} className={cn("px-4 py-3 text-right tabular-nums font-semibold", b === "Other" ? "text-muted-foreground" : "text-foreground")}>
                  {footer.sovByBrand[b] > 0 ? fmtPct(footer.sovByBrand[b]) : <span className="text-muted-foreground">—</span>}
                </TableCell>
              ))}
            </TableRow>
          </TableFooter>
        </Table>
      )}
    </div>
  );
}
